/**
 * GroupDrafts — public, fully ToS-compliant Facebook Group Draft Library.
 *
 * Member-owned-group posting via the Graph API is prohibited unless the
 * caller is a group admin. So instead of automating, we generate fresh
 * Canadian-landlord posts daily and surface them here for visitors to
 * COPY + PASTE into the FB groups they're already in. This bypasses
 * traditional search by engaging communities directly — same outcome,
 * zero ToS risk.
 */
import { useEffect, useState } from "react";
import { Copy, Check, RefreshCw, Sparkles } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import SmartChatBubble from "@/components/SmartChatBubble";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function setMeta(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement(sel.startsWith("link") ? "link" : "meta"); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function CopyCard({ draft }) {
  const [done, setDone] = useState(false);
  const [claimed, setClaimed] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(draft.body);
      setDone(true);
      setTimeout(() => setDone(false), 1800);
      // Best-effort claim ping (anonymous OK).
      try {
        await fetch(`${API}/api/social/group-drafts/${draft.id}/claim?email=anonymous@library`, { method: "POST" });
        setClaimed(true);
      } catch { /* noop */ }
    } catch { /* noop */ }
  }
  const kindLabel =
    draft.kind === "tip" ? "Landlord Tip" :
    draft.kind === "testimonial_or_compare" ? "Social Proof" :
    draft.kind === "blog" ? "Article Promo" : draft.kind || "Post";

  return (
    <article data-testid={`group-draft-${draft.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{kindLabel}</span>
        <span className="text-[10px] text-slate-400">{new Date(draft.created_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}</span>
      </div>
      <div className="font-bold text-slate-900 text-sm leading-snug mb-2 line-clamp-2">{draft.topic}</div>
      <pre className="flex-1 text-[12px] whitespace-pre-wrap font-sans text-slate-700 bg-slate-50 border border-slate-100 rounded-lg p-3 max-h-56 overflow-y-auto">{draft.body}</pre>
      <button
        onClick={copy}
        data-testid={`copy-draft-${draft.id}`}
        className={`mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold ${done ? "bg-emerald-600 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
      >
        {done ? <><Check className="w-4 h-4"/> Copied — paste into your group</> : <><Copy className="w-4 h-4"/> Copy paste-ready post</>}
      </button>
      {claimed && <div className="mt-2 text-[10px] text-slate-400 text-center">Logged · keeps the queue fresh</div>}
    </article>
  );
}

export default function GroupDrafts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");

  async function load() {
    setLoading(true);
    try {
      const u = new URL(`${API}/api/social/group-drafts`);
      u.searchParams.set("limit", "24");
      if (kind) u.searchParams.set("kind", kind);
      const r = await fetch(u.toString());
      const d = await r.json();
      setItems(d.items || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [kind]);

  useEffect(() => {
    document.title = "Group Posts Library — Central Rentals";
    const desc = "Copy-paste-ready posts for your favourite Canadian landlord Facebook groups. Refreshed daily by Mark.";
    const tags = [
      setMeta('meta[name="description"]', { name: "description", content: desc }),
      setMeta('link[rel="canonical"]', { rel: "canonical", href: "https://centralrentals.ca/group-drafts" }),
    ];
    return () => tags.forEach((t) => t?.remove?.());
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="group-drafts-page">
      <PublicHeader light/>

      <section className="max-w-6xl mx-auto px-5 pt-12 pb-6">
        <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-200 text-[11px] font-bold tracking-wide text-violet-800">
          <Sparkles className="w-3.5 h-3.5"/> Refreshed every day by Mark
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-slate-900">
          Free posts. Drop them in your Facebook groups.
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl">
          Member-owned groups can&apos;t be auto-posted to under Meta&apos;s rules — so we do the
          next best thing. Every day Mark generates fresh, ready-to-paste posts that
          actually help your community (landlord tips, social proof, side-by-sides).
          Pick one, hit copy, paste into your group. Done in under 10 seconds.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="group-drafts-filter">
          {[
            { v: "",                        l: "All" },
            { v: "tip",                     l: "Landlord tips" },
            { v: "testimonial_or_compare",  l: "Social proof" },
            { v: "blog",                    l: "Article promos" },
          ].map((b) => (
            <button
              key={b.v || "all"}
              onClick={() => setKind(b.v)}
              data-testid={`group-drafts-filter-${b.v || "all"}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${kind === b.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
            >
              {b.l}
            </button>
          ))}
          <button onClick={load} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold text-slate-600 hover:bg-slate-100">
            <RefreshCw className="w-3 h-3"/> Refresh
          </button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-16">
        {loading ? (
          <div className="text-center py-16 text-slate-500 text-sm">Loading paste-ready posts…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center text-slate-500">
            <div className="font-bold text-slate-700">No drafts yet — check back in a few hours.</div>
            <div className="text-xs mt-1">Mark publishes fresh posts twice a day.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="group-drafts-grid">
            {items.map((d) => <CopyCard key={d.id} draft={d}/>)}
          </div>
        )}
      </section>

      <SmartChatBubble/>
    </div>
  );
}
